/**
 * Movie suggestions page
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import apiService from '../services/api';
import MovieRatingDialog from '../components/MovieRatingDialog';
import StarRating from '../components/StarRating';
import AddToWatchlistModal from '../components/AddToWatchlistModal';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useRealtimeRefetch } from '../hooks/useRealtimeRefetch';
import styled, { keyframes, css } from 'styled-components';

const PROVIDER_NAMES: Record<number, string> = {
  8: 'Netflix', 9: 'Amazon Prime Video', 337: 'Disney+', 2: 'Apple TV+',
  384: 'Max', 15: 'Hulu', 386: 'Peacock', 531: 'Paramount+',
  11: 'Mubi', 258: 'Criterion Channel',
  73: 'Tubi', 300: 'Pluto TV', 207: 'The Roku Channel',
  538: 'Plex', 613: 'Amazon Freevee', 192: 'YouTube Free',
};

const GENRE_NAMES: Record<number, string> = {
  28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy',
  80: 'Crime', 99: 'Documentary', 18: 'Drama', 10751: 'Family',
  14: 'Fantasy', 36: 'History', 27: 'Horror', 10402: 'Music',
  9648: 'Mystery', 10749: 'Romance', 878: 'Science Fiction',
  10770: 'TV Movie', 53: 'Thriller', 10752: 'War', 37: 'Western',
};

type GroupBy = 'none' | 'movie' | 'director' | 'genre';
type SortBy = 'points' | 'title' | 'genre' | 'director' | 'score';
type Method = 'all' | 'similar' | 'director' | 'actor' | 'studio';

// --- Layout ---

const SuggestionsContainer = styled.div`
  max-width: 1350px;
  margin: 0 auto;
`;

const PageLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const FilterPane = styled.aside`
  width: 230px;
  flex-shrink: 0;
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
  position: sticky;
  top: 1rem;
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

// --- Filter pane internals ---

const FilterPaneHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--color-border);
`;

const FilterPaneTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  color: var(--color-accent);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;

  &:hover { text-decoration: underline; }
  &:disabled { color: var(--color-text-muted); cursor: default; text-decoration: none; }
`;

const FilterGroup = styled.div`
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const FilterGroupTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover { color: var(--color-text-secondary); }

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.90);
  }
  [data-theme="dark"] &:hover {
    color: rgba(255, 255, 255, 1.0);
  }
`;

const CollapseArrow = styled.span<{ $open: boolean }>`
  font-size: 0.6rem;
  transition: transform 0.15s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: inline-block;
`;

const FilterGroupBody = styled.div<{ $open: boolean }>`
  margin-top: ${p => p.$open ? '0.6rem' : '0'};
  display: ${p => p.$open ? 'block' : 'none'};
`;

const YearRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
`;

const YearInput = styled.input`
  width: 72px;
  padding: 0.4rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.9rem;
  color: var(--color-text);
  background: var(--color-surface);

  &:focus { outline: none; border-color: var(--color-accent); }
  &::-webkit-inner-spin-button { opacity: 0.4; }
`;

const SliderLabel = styled.div`
  font-size: 0.82rem;
  color: var(--color-text);
  margin-bottom: 0.35rem;
`;

const ScoreSlider = styled.input`
  width: 100%;
  accent-color: var(--color-accent);
  cursor: pointer;
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 170px;
  overflow-y: auto;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.83rem;
  color: var(--color-text);
  cursor: pointer;
  user-select: none;

  &:hover { color: var(--color-text); }

  input { accent-color: var(--color-accent); flex-shrink: 0; }

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.55);
  }
  [data-theme="dark"] &:hover {
    color: rgba(255, 255, 255, 0.80);
  }
`;

const FilterResultCount = styled.div`
  font-size: 0.78rem;
  color: var(--color-text-muted);
  text-align: center;
  margin-top: 0.75rem;
  font-style: italic;
`;

const EmptyFilterNote = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-style: italic;
`;

// --- Main page components ---

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  color: var(--color-text);
  margin: 0;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-secondary);
  cursor: pointer;
`;

const Checkbox = styled.input`
  margin: 0;
`;

const StatsCard = styled.div`
  background: var(--color-surface);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  margin-bottom: 1.5rem;
  text-align: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1.5rem;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--color-accent);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
`;

const SuggestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const spinBorder = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const RatingTierWrapper = styled.div<{ $tier: 'bronze' | 'silver' | 'gold' }>`
  border-radius: 13px;
  padding: 2px;
  position: relative;

  ${p => p.$tier === 'bronze' && css`
    background: linear-gradient(135deg, #7a3a10, #cd7f32, #f0a860, #cd7f32, #7a3a10);
    box-shadow: 0 4px 14px rgba(205, 127, 50, 0.25);
  `}
  ${p => p.$tier === 'silver' && css`
    background: linear-gradient(135deg, #4a4c50, #a8a9ad, #d8d9db, #a8a9ad, #4a4c50);
    box-shadow: 0 4px 14px rgba(168, 169, 173, 0.30);
  `}
  ${p => p.$tier === 'gold' && css`
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(212, 175, 55, 0.45);
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 200%;
      height: 200%;
      transform-origin: 0 0;
      animation: ${spinBorder} 6s linear infinite;
      background: conic-gradient(
        #5a3e00 0%, #d4af37 15%, #f9e97e 25%, #ffffff 30%,
        #f9e97e 35%, #d4af37 50%, #5a3e00 65%,
        #d4af37 80%, #f9e97e 90%, #ffffff 95%, #d4af37 100%
      );
    }
  `}
`;

const SuggestionCard = styled.div<{ $tiered?: boolean }>`
  background: var(--color-surface);
  border-radius: ${p => p.$tiered ? '11px' : '12px'};
  box-shadow: ${p => p.$tiered ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  overflow: hidden;
  transition: transform 0.2s;
  ${p => p.$tiered && css`position: relative; z-index: 1;`}

  &:hover {
    transform: translateY(-2px);
  }
`;

const StarIcon = styled.span<{ $tier: 'none' | 'bronze' | 'silver' | 'gold' }>`
  color: ${p => ({none: '#aaa', bronze: '#cd7f32', silver: '#a8a9ad', gold: '#d4af37'}[p.$tier])};
`;

const SourceBadge = styled.span`
  background: var(--color-badge-source-bg);
  color: var(--color-badge-source-text);
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  white-space: nowrap;
  vertical-align: middle;
`;

const MovieInfo = styled.div`
  padding: 1rem 1.25rem;
`;

const MovieTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
`;

const TitleLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: var(--color-accent);
  }
`;

const MovieDetails = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const MovieOverview = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProviderList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const ProviderBadge = styled.span`
  background: var(--color-badge-provider-bg);
  color: var(--color-badge-provider-text);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  border: 1px solid var(--color-badge-provider-border);
  white-space: nowrap;
`;

const NoProviderNote = styled.div`
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-style: italic;
  margin-bottom: 1rem;
`;

const PointsBadge = styled.span<{ $score: number }>`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  vertical-align: middle;
  background: ${p => p.$score > 0 ? '#c6f6d5' : p.$score < 0 ? '#fed7d7' : '#f0f0f0'};
  color: ${p => p.$score > 0 ? '#276749' : p.$score < 0 ? '#c53030' : '#666'};

  [data-theme="dark"] & {
    background: ${p => p.$score > 0 ? 'rgba(39, 103, 73, 0.50)' : p.$score < 0 ? 'rgba(197, 48, 48, 0.40)' : 'rgba(255, 255, 255, 0.10)'};
    color: ${p => p.$score > 0 ? '#68d391' : p.$score < 0 ? '#fc8181' : 'rgba(255,255,255,0.55)'};
  }
`;

const WatchlistFlag = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--color-badge-provider-text);
  background: var(--color-badge-provider-bg);
  border: 1px solid var(--color-badge-provider-border);
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  margin-left: 0.4rem;
  vertical-align: middle;
  white-space: nowrap;
`;

const ProviderToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--color-accent);
  font-size: 0.78rem;
  cursor: pointer;
  margin-bottom: 0.75rem;
  display: block;

  &:hover {
    text-decoration: underline;
  }
`;

const BasedOnNote = styled.div`
  color: var(--color-text-muted);
  font-size: 0.8rem;
  font-style: italic;
  margin-bottom: 0.75rem;
`;

const BasedOnLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: var(--color-accent);
  font-size: 0.8rem;
  font-style: italic;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupBox = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 1.5rem;
  width: 90%;
  max-width: 340px;
  max-height: 60vh;
  overflow-y: auto;
  box-shadow: var(--shadow-card);
`;

const PopupTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: var(--color-text);
  font-size: 1rem;
`;

const PopupList = styled.ul`
  margin: 0 0 1.25rem 0;
  padding: 0 0 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const PopupCloseButton = styled.button`
  background: var(--color-accent);
  color: white;
  border: none;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: var(--color-accent-hover);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ReasonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
  align-items: center;
`;

const ReasonLabel = styled.span`
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  margin-right: 0.25rem;
`;

const ReasonChip = styled.button`
  padding: 0.25rem 0.65rem;
  border: 1px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover { background: var(--color-accent-subtle); border-color: var(--color-accent); color: var(--color-accent); }
`;

const SkipChip = styled(ReasonChip)`
  color: var(--color-text-muted);
  border-style: dashed;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: background 0.2s, color 0.2s;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--color-accent);
          color: white;
          &:hover { background: var(--color-accent-hover); }
        `;
      case 'secondary':
        return `
          background: transparent;
          color: var(--color-accent);
          border: 1px solid var(--color-accent);
          &:hover { background: var(--color-accent-subtle); }
        `;
      case 'danger':
        return `
          background: #e53e3e;
          color: white;
          &:hover { background: #c53030; }
        `;
      default:
        return `
          background: var(--color-bg);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          &:hover { background: var(--color-border); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CardButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.6rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
  white-space: nowrap;
  line-height: 1;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--color-accent);
          color: white;
          transition: background 0.2s;
          &:hover { background: var(--color-accent-hover); }
        `;
      case 'danger':
        return `
          background: #e53e3e;
          color: white;
          transition: background 0.2s;
          &:hover { background: #c53030; }
        `;
      default:
        return `
          background: var(--color-bg);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          transition: background 0.2s;
          &:hover { background: var(--color-border); }

          [data-theme="dark"] & {
            background: rgba(255, 255, 255, 0.09);
            border-color: rgba(255, 255, 255, 0.22);
          }
          [data-theme="dark"] &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.16);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnLabel = styled.span<{ $always?: boolean }>`
  max-width: ${p => p.$always ? '8rem' : '0'};
  overflow: hidden;
  opacity: ${p => p.$always ? '1' : '0'};
  margin-left: ${p => p.$always ? '0.3rem' : '0'};
  transition: max-width 0.2s ease, opacity 0.15s ease, margin-left 0.2s ease;

  ${CardButton}:hover:not(:disabled) & {
    max-width: 8rem;
    opacity: 1;
    margin-left: 0.3rem;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const NoSuggestions = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-text-secondary);
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
`;

const GroupByBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  position: sticky;
  top: 0;
  background: var(--color-bg);
  z-index: 10;
  padding: 0.6rem 0;
`;

const GroupBySelect = styled.select`
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.9rem;
  color: var(--color-text);
  background: var(--color-surface);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const GroupSection = styled.div`
  margin-bottom: 2.5rem;
`;

const GroupHeader = styled.h2`
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;

  &:hover { color: var(--color-accent); }
`;

const ExpandCollapseButton = styled.button`
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: var(--color-accent-subtle); border-color: var(--color-accent); color: var(--color-accent); }
`;

const GroupCount = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--color-text-muted);
`;

const ProgressBar = styled.div`
  width: 100%;
  background: var(--color-border);
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  background: var(--color-primary);
  border-radius: 4px;
  width: ${p => p.$pct}%;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: 4px;
  text-align: center;
`;

// --- Types ---

interface WatchProvider {
  id: number;
  name: string;
}

interface Suggestion {
  id: string;
  suggestion_id: number;
  user_id: string;
  title: string;
  tmdb_id: number;
  release_date?: string;
  runtime?: number;
  tmdb_genres?: number[];
  rt_rating?: number;
  mc_rating?: number;
  imdb_score?: number;
  tmdb_score?: number;
  suggestion_date: string;
  suggestion_source: string;
  is_watched: boolean;
  is_rejected: boolean;
  cast_names?: string;
  director_name?: string;
  overview?: string;
  watch_providers?: WatchProvider[];
  based_on_title?: string;
  based_on_tmdb_id?: number;
  points?: number;
  mpaa_rating?: string;
}

// --- Helpers ---

type RatingTier = 'none' | 'bronze' | 'silver' | 'gold';

const getRatingTier = (score?: number): RatingTier => {
  if (!score) return 'none';
  if (score >= 8.5) return 'gold';
  if (score >= 8.0) return 'silver';
  if (score >= 7.0) return 'bronze';
  return 'none';
};

// --- Component ---

const SuggestionsPage: React.FC = () => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeWatched, setIncludeWatched] = useState(false);
  const [includeRejected, setIncludeRejected] = useState(false);
  const [filterByProviders, setFilterByProviders] = useState(true);
  const [selectedMovie, setSelectedMovie] = useState<any>(null);
  const pendingRateSuggestionId = useRef<number | null>(null);
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<{ done: number; total: number } | null>(null);
  const cancelRef = useRef(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [method, setMethod] = useState<Method>('all');
  const [sortBy, setSortBy] = useState<SortBy>('points');
  const [collapsedResultGroups, setCollapsedResultGroups] = useState<Set<string>>(new Set());

  const toggleResultGroup = (key: string) =>
    setCollapsedResultGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const expandAll = () => setCollapsedResultGroups(new Set());
  const collapseAll = () => setCollapsedResultGroups(new Set(groupedSuggestions.map(([k]) => k)));
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const savedScrollY = useRef<number | null>(null);

  // "Because you liked" overflow popup, when a suggestion has more than 2 contributing movies
  const [basedOnPopup, setBasedOnPopup] = useState<{ label: string; movies: string[] } | null>(null);

  // Watchlist state
  const [watchlistMovie, setWatchlistMovie] = useState<Suggestion | null>(null);
  // tmdb_id → list of watchlist names the movie appears in
  const [watchlistMembership, setWatchlistMembership] = useState<Record<number, string[]>>({});

  // Rejection reason flow: suggestion_id waiting for reason selection
  const [pendingRejectId, setPendingRejectId] = useState<number | null>(null);

  // Side-pane filter state
  const [filterMinYear, setFilterMinYear] = useState<number | ''>('');
  const [filterMaxYear, setFilterMaxYear] = useState<number | ''>('');
  const [filterMinScore, setFilterMinScore] = useState(0);
  const [filterMinPoints, setFilterMinPoints] = useState<number | null>(null);
  const [filterProviders, setFilterProviders] = useState<Set<number>>(new Set());
  const [filterGenres, setFilterGenres] = useState<Set<number>>(new Set());
  const [filterMpaaRatings, setFilterMpaaRatings] = useState<Set<string>>(new Set());

  // Clear provider selection whenever the "My providers only" toggle changes
  useEffect(() => {
    setFilterProviders(new Set());
  }, [filterByProviders]);

  const toggleProviders = (id: string) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleFilterProvider = (id: number) => {
    setFilterProviders(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleFilterGenre = (id: number) => {
    setFilterGenres(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (name: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleFilterMpaa = (rating: string) => {
    setFilterMpaaRatings(prev => {
      const next = new Set(prev);
      next.has(rating) ? next.delete(rating) : next.add(rating);
      return next;
    });
  };

  const clearFilters = () => {
    setFilterMinYear('');
    setFilterMaxYear('');
    setFilterMinScore(0);
    setFilterMinPoints(null);
    setFilterProviders(new Set());
    setFilterGenres(new Set());
    setFilterMpaaRatings(new Set());
  };

  const hasActiveFilters =
    filterMinYear !== '' || filterMaxYear !== '' || filterMinScore > 0 ||
    filterMinPoints !== null || filterProviders.size > 0 || filterGenres.size > 0 ||
    filterMpaaRatings.size > 0;

  useEffect(() => {
    if (savedScrollY.current !== null) {
      window.scrollTo(0, savedScrollY.current);
      savedScrollY.current = null;
    }
  }, [suggestions]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError('');
    try {
      const [response, membership] = await Promise.all([
        apiService.getMovieSuggestions(1, 750, includeWatched, includeRejected),
        apiService.getWatchlistMembership().catch(() => ({} as Record<number, string[]>)),
      ]);
      const seen = new Set<number>();
      const deduped = response.items.filter(s => {
        if (seen.has(s.tmdb_id)) return false;
        seen.add(s.tmdb_id);
        return true;
      });
      setSuggestions(deduped);
      setWatchlistMembership(membership);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [includeWatched, includeRejected]);

  // Watchlist badges on cards follow watchlist changes made on other pages/devices
  useRealtimeRefetch(['watchlist_movies'], () => {
    apiService.getWatchlistMembership().then(setWatchlistMembership).catch(() => {});
  });

  // Live updates: suggestions inserted/removed by the background rating-trigger (or another
  // tab/device) appear here without a manual refresh. Requires the supabase-js client's session
  // to be synced with the app's own auth tokens (see api.ts syncSupabaseSession) so Realtime's
  // RLS check on movie_suggestions (auth.uid() = user_id) passes.
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`movie_suggestions_${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'movie_suggestions', filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as Suggestion;
            setSuggestions(prev => (prev.some(s => s.tmdb_id === row.tmdb_id) ? prev : [...prev, row]));
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as Suggestion;
            setSuggestions(prev => prev.map(s => (s.id === row.id ? row : s)));
          } else if (payload.eventType === 'DELETE') {
            const oldId = (payload.old as { id?: string }).id;
            if (!oldId) return;
            setSuggestions(prev => prev.filter(s => s.id !== oldId));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleCancel = () => { cancelRef.current = true; };

  const handleRefreshProviders = async () => {
    cancelRef.current = false;
    setRefreshing(true);
    setActionMessage('');
    setError('');
    setRefreshProgress(null);
    let totalUpdated = 0;
    try {
      let offset = 0;
      while (!cancelRef.current) {
        const result = await apiService.refreshProviders(offset);
        totalUpdated += result.updated;
        setRefreshProgress({ done: result.processed, total: result.total });
        if (!result.has_more) break;
        offset = result.processed;
      }
      const msg = cancelRef.current
        ? `Cancelled — updated providers for ${totalUpdated} suggestions so far`
        : `Updated providers for ${totalUpdated} suggestions`;
      setActionMessage(msg);
      fetchSuggestions();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to refresh provider data');
    } finally {
      setRefreshing(false);
      setRefreshProgress(null);
    }
  };

  const handleMarkAsWatched = async (suggestionId: number) => {
    setActionLoading(`watched-${suggestionId}`);
    try {
      await apiService.updateSuggestion(suggestionId, { is_watched: true });
      savedScrollY.current = window.scrollY;
      setSuggestions(prev => includeWatched
        ? prev.map(s => s.suggestion_id === suggestionId ? { ...s, is_watched: true } : s)
        : prev.filter(s => s.suggestion_id !== suggestionId)
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to mark as watched');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (suggestionId: number, reason?: string) => {
    setPendingRejectId(null);
    setActionLoading(`reject-${suggestionId}`);
    try {
      const updateData: { is_rejected: boolean; rejection_reason?: string } = { is_rejected: true };
      if (reason) updateData.rejection_reason = reason;
      await apiService.updateSuggestion(suggestionId, updateData);
      savedScrollY.current = window.scrollY;
      setSuggestions(prev => includeRejected
        ? prev.map(s => s.suggestion_id === suggestionId ? { ...s, is_rejected: true } : s)
        : prev.filter(s => s.suggestion_id !== suggestionId)
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reject suggestion');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRateMovie = (suggestion: Suggestion) => {
    const movieData = {
      tmdb_id: suggestion.tmdb_id,
      title: suggestion.title,
      release_date: suggestion.release_date,
      overview: suggestion.overview || '',
      poster_path: undefined,
      genre_ids: suggestion.tmdb_genres,
      vote_average: suggestion.imdb_score || 0,
      vote_count: 0,
    };
    pendingRateSuggestionId.current = suggestion.suggestion_id;
    setSelectedMovie(movieData);
    setIsRatingDialogOpen(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).getFullYear();
  };


  const userProviders: number[] = user?.movie_providers ?? [];
  const hasProviderPrefs = userProviders.length > 0;

  const visibleSuggestions = useMemo(() =>
    filterByProviders && hasProviderPrefs
      ? suggestions.filter(s =>
          Array.isArray(s.watch_providers) &&
          s.watch_providers.some(p => userProviders.includes(p.id))
        )
      : suggestions,
    [suggestions, filterByProviders, hasProviderPrefs, userProviders]
  );

  const activeSuggestions = visibleSuggestions.filter(s => !s.is_watched && !s.is_rejected);
  const watchedCount = visibleSuggestions.filter(s => s.is_watched).length;
  const rejectedCount = visibleSuggestions.filter(s => s.is_rejected).length;

  // Providers and genres that actually appear in loaded suggestions
  const availableProviders = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of suggestions) {
      for (const p of s.watch_providers ?? []) {
        if (!map.has(p.id)) map.set(p.id, p.name);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [suggestions]);

  const availableGenres = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of suggestions) {
      for (const gid of s.tmdb_genres ?? []) {
        const name = GENRE_NAMES[gid];
        if (name) map.set(gid, name);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [suggestions]);

  // When "My providers only" is on, build the list from user's Settings preferences directly
  // (so it's populated even before suggestion watch_providers data is loaded)
  const paneProviders = useMemo(() => {
    if (filterByProviders && hasProviderPrefs) {
      const fromSuggestions = new Map(availableProviders.map(p => [p.id, p.name]));
      return userProviders
        .map(id => ({ id, name: fromSuggestions.get(id) ?? PROVIDER_NAMES[id] ?? `Provider ${id}` }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return availableProviders;
  }, [availableProviders, filterByProviders, hasProviderPrefs, userProviders]);

  // 'similar' covers both the current 'similar_movies' source value and the legacy raw 'similar'
  // value from older suggestion rows generated before that mapping was fixed.
  const methodMatchesSource = (m: Method, source: string) => {
    if (m === 'all') return true;
    if (m === 'similar') return source === 'similar_movies' || source === 'similar';
    return source === m;
  };

  // Apply side-pane filters on top of the provider-preference filter
  const filteredSuggestions = useMemo(() => {
    return visibleSuggestions.filter(s => {
      if (!methodMatchesSource(method, s.suggestion_source)) return false;
      const year = s.release_date ? parseInt(s.release_date.slice(0, 4), 10) : null;
      if (filterMinYear !== '' && year !== null && year < filterMinYear) return false;
      if (filterMaxYear !== '' && year !== null && year > filterMaxYear) return false;
      if (filterMinScore > 0 && (s.imdb_score ?? 0) < filterMinScore) return false;
      if (filterMinPoints !== null && (s.points ?? 0) < filterMinPoints) return false;
      if (filterProviders.size > 0) {
        const pids = new Set((s.watch_providers ?? []).map(p => p.id));
        if (!Array.from(filterProviders).some(pid => pids.has(pid))) return false;
      }
      if (filterGenres.size > 0) {
        const gids = new Set(s.tmdb_genres ?? []);
        if (!Array.from(filterGenres).some(gid => gids.has(gid))) return false;
      }
      if (filterMpaaRatings.size > 0 && s.mpaa_rating && !filterMpaaRatings.has(s.mpaa_rating)) return false;
      return true;
    });
  }, [visibleSuggestions, method, filterMinYear, filterMaxYear, filterMinScore, filterMinPoints, filterProviders, filterGenres, filterMpaaRatings]);

  const sortedSuggestions = useMemo(() => {
    const arr = [...filteredSuggestions];
    switch (sortBy) {
      case 'title':
        return arr.sort((a, b) => a.title.localeCompare(b.title));
      case 'genre': {
        const genreName = (s: Suggestion) => {
          const gid = s.tmdb_genres?.[0];
          return gid ? (GENRE_NAMES[gid] || 'ZZZ') : 'ZZZ';
        };
        return arr.sort((a, b) => genreName(a).localeCompare(genreName(b)) || a.title.localeCompare(b.title));
      }
      case 'director':
        return arr.sort((a, b) =>
          (a.director_name || 'ZZZ').localeCompare(b.director_name || 'ZZZ') || a.title.localeCompare(b.title)
        );
      case 'score':
        return arr.sort((a, b) => (b.imdb_score ?? 0) - (a.imdb_score ?? 0));
      default: // 'points' — preserve backend order (highest points first)
        return arr;
    }
  }, [filteredSuggestions, sortBy]);

  const getGroupKey = (s: Suggestion): string => {
    switch (groupBy) {
      case 'movie':    return s.based_on_title || 'Other';
      case 'director': return s.director_name || 'Unknown Director';
      case 'genre': {
        const gid = s.tmdb_genres?.[0];
        return gid ? (GENRE_NAMES[gid] || 'Other') : 'Other';
      }
      default: return 'all';
    }
  };

  const groupedSuggestions: Array<[string, Suggestion[]]> = useMemo(() => {
    const map = new Map<string, Suggestion[]>();
    for (const s of sortedSuggestions) {
      const key = getGroupKey(s);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return Array.from(map.entries());
  }, [sortedSuggestions, groupBy]);

  const renderCard = (suggestion: Suggestion) => {
    const tier = getRatingTier(suggestion.imdb_score);
    const genreLabel = (suggestion.tmdb_genres ?? [])
      .slice(0, 2)
      .map(gid => GENRE_NAMES[gid])
      .filter(Boolean)
      .join(', ');
    const inner = (
      <SuggestionCard $tiered={tier !== 'none'}>
        <MovieInfo>
        <MovieTitle>
          <TitleLink
            href={`https://www.themoviedb.org/movie/${suggestion.tmdb_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {suggestion.title}
          </TitleLink>
          {suggestion.points != null && (
            <PointsBadge $score={suggestion.points}>
              {suggestion.points > 0 ? `+${suggestion.points}` : suggestion.points} pts
            </PointsBadge>
          )}
          {watchlistMembership[suggestion.tmdb_id]?.map(name => (
            <WatchlistFlag key={name}>{name}</WatchlistFlag>
          ))}
        </MovieTitle>
        <MovieDetails>
          {formatDate(suggestion.release_date)} |
          {suggestion.runtime && ` ${suggestion.runtime}m |`}
          {suggestion.imdb_score && (
            <span>
              {' '}
              <StarIcon $tier={tier}>★</StarIcon> {suggestion.imdb_score}
              {suggestion.tmdb_score != null && ` (${suggestion.tmdb_score.toFixed(1)})`}
            </span>
          )}
          {suggestion.mpaa_rating && <span> · {suggestion.mpaa_rating}</span>}
        </MovieDetails>
        {genreLabel && <MovieDetails>{genreLabel}</MovieDetails>}
        {suggestion.director_name && (
          <MovieDetails>Director: {suggestion.director_name}</MovieDetails>
        )}
        {suggestion.cast_names && (
          <MovieOverview>Cast: {suggestion.cast_names}</MovieOverview>
        )}
        {suggestion.based_on_title && (() => {
          const label = suggestion.suggestion_source === 'genre' ? 'Recommended for' : 'Because you liked';
          const basedOnMovies = suggestion.based_on_title.split(', ').filter(Boolean);
          if (basedOnMovies.length <= 2) {
            return <BasedOnNote>{label}: {suggestion.based_on_title}</BasedOnNote>;
          }
          return (
            <BasedOnNote>
              {label}: {basedOnMovies.slice(0, 2).join(', ')}{' '}
              <BasedOnLink onClick={() => setBasedOnPopup({ label, movies: basedOnMovies })}>
                and {basedOnMovies.length - 2} more
              </BasedOnLink>
            </BasedOnNote>
          );
        })()}
        {Array.isArray(suggestion.watch_providers) && (
          suggestion.watch_providers.length > 0 ? (
            <>
              <ProviderToggle onClick={() => toggleProviders(suggestion.id)}>
                {expandedProviders.has(suggestion.id)
                  ? '▾ Hide providers'
                  : `▸ Streaming Service (${suggestion.watch_providers.length})`}
              </ProviderToggle>
              {expandedProviders.has(suggestion.id) && (
                <ProviderList>
                  {suggestion.watch_providers.map(p => (
                    <ProviderBadge key={p.id}>{p.name}</ProviderBadge>
                  ))}
                </ProviderList>
              )}
            </>
          ) : (
            <NoProviderNote>Not available on US streaming</NoProviderNote>
          )
        )}

        <ButtonGroup>
          <CardButton $variant="primary" onClick={() => handleRateMovie(suggestion)}>
            <span>★</span><BtnLabel>Rate Movie</BtnLabel>
          </CardButton>
          <CardButton onClick={() => setWatchlistMovie(suggestion)}>
            <span>+</span><BtnLabel>Watchlist</BtnLabel>
          </CardButton>
          {!suggestion.is_watched && (
            <CardButton
              onClick={() => handleMarkAsWatched(suggestion.suggestion_id)}
              disabled={actionLoading === `watched-${suggestion.suggestion_id}`}
            >
              <span>✓</span>
              {actionLoading === `watched-${suggestion.suggestion_id}`
                ? <BtnLabel $always>…</BtnLabel>
                : <BtnLabel>Mark Watched</BtnLabel>
              }
            </CardButton>
          )}
          {!suggestion.is_rejected && (
            <CardButton
              $variant="danger"
              onClick={() => setPendingRejectId(suggestion.suggestion_id)}
              disabled={actionLoading === `reject-${suggestion.suggestion_id}`}
            >
              <span>✕</span>
              {actionLoading === `reject-${suggestion.suggestion_id}`
                ? <BtnLabel $always>…</BtnLabel>
                : <BtnLabel>Not Interested</BtnLabel>
              }
            </CardButton>
          )}
        </ButtonGroup>

        {pendingRejectId === suggestion.suggestion_id && (
          <ReasonRow>
            <ReasonLabel>Why not?</ReasonLabel>
            {['Already seen it', 'Not my genre', 'Dislike the cast', 'Heard bad things', 'Too dark / violent'].map(reason => (
              <ReasonChip key={reason} onClick={() => handleReject(suggestion.suggestion_id, reason)}>
                {reason}
              </ReasonChip>
            ))}
            <SkipChip onClick={() => handleReject(suggestion.suggestion_id)}>Skip</SkipChip>
          </ReasonRow>
        )}
      </MovieInfo>
    </SuggestionCard>
    );
    if (tier === 'none') return <React.Fragment key={suggestion.id}>{inner}</React.Fragment>;
    return (
      <RatingTierWrapper key={suggestion.id} $tier={tier}>
        {inner}
      </RatingTierWrapper>
    );
  };

  return (
    <SuggestionsContainer>
      <PageLayout>
        {/* Left filter pane */}
        <FilterPane>
          <FilterPaneHeader>
            <FilterPaneTitle>Filters</FilterPaneTitle>
            <ClearAllButton onClick={clearFilters} disabled={!hasActiveFilters}>
              Clear all
            </ClearAllButton>
          </FilterPaneHeader>

          {/* Visibility toggles */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('show')}>
              Show
              <CollapseArrow $open={!collapsedGroups.has('show')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('show')}>
              {hasProviderPrefs && (
                <CheckRow>
                  <input
                    type="checkbox"
                    checked={filterByProviders}
                    onChange={e => setFilterByProviders(e.target.checked)}
                  />
                  My providers only
                </CheckRow>
              )}
              <CheckRow>
                <input
                  type="checkbox"
                  checked={includeWatched}
                  onChange={e => setIncludeWatched(e.target.checked)}
                />
                Watched
              </CheckRow>
              <CheckRow>
                <input
                  type="checkbox"
                  checked={includeRejected}
                  onChange={e => setIncludeRejected(e.target.checked)}
                />
                Rejected
              </CheckRow>
            </FilterGroupBody>
          </FilterGroup>

          {/* Year range */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('year')}>
              Year
              <CollapseArrow $open={!collapsedGroups.has('year')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('year')}>
              <YearRow>
                <YearInput
                  type="number"
                  placeholder="From"
                  value={filterMinYear}
                  min={1900}
                  max={2030}
                  onChange={e => setFilterMinYear(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                />
                <span>–</span>
                <YearInput
                  type="number"
                  placeholder="To"
                  value={filterMaxYear}
                  min={1900}
                  max={2030}
                  onChange={e => setFilterMaxYear(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                />
              </YearRow>
            </FilterGroupBody>
          </FilterGroup>

          {/* IMDb score */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('imdb')}>
              IMDb Score
              <CollapseArrow $open={!collapsedGroups.has('imdb')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('imdb')}>
              <SliderLabel>
                {filterMinScore === 0 ? 'Any' : `≥ ${filterMinScore.toFixed(1)}`}
              </SliderLabel>
              <ScoreSlider
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={filterMinScore}
                onChange={e => setFilterMinScore(parseFloat(e.target.value))}
              />
            </FilterGroupBody>
          </FilterGroup>

          {/* Points filter */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('points')}>
              Points
              <CollapseArrow $open={!collapsedGroups.has('points')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('points')}>
              <select
                value={filterMinPoints ?? ''}
                onChange={e => setFilterMinPoints(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                style={{
                  width: '100%', padding: '0.3rem 0.4rem', border: '1px solid var(--color-border)',
                  borderRadius: '6px', fontSize: '0.82rem', color: 'var(--color-text)', background: 'var(--color-surface)',
                }}
              >
                <option value="">Any</option>
                <option value="1">≥ +1 pt</option>
                <option value="2">≥ +2 pts</option>
                <option value="3">≥ +3 pts</option>
              </select>
            </FilterGroupBody>
          </FilterGroup>

          {/* Provider filter */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('provider')}>
              Streaming Services
              <CollapseArrow $open={!collapsedGroups.has('provider')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('provider')}>
              {paneProviders.length === 0 ? (
                <EmptyFilterNote>
                  {filterByProviders && hasProviderPrefs
                    ? 'No preferred providers found in suggestions'
                    : 'Load suggestions first'}
                </EmptyFilterNote>
              ) : (
                <CheckList>
                  {paneProviders.map(p => (
                    <CheckRow key={p.id}>
                      <input
                        type="checkbox"
                        checked={filterProviders.has(p.id)}
                        onChange={() => toggleFilterProvider(p.id)}
                      />
                      {p.name}
                    </CheckRow>
                  ))}
                </CheckList>
              )}
            </FilterGroupBody>
          </FilterGroup>

          {/* Genre filter */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('genre')}>
              Genre
              <CollapseArrow $open={!collapsedGroups.has('genre')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('genre')}>
              {availableGenres.length === 0 ? (
                <EmptyFilterNote>Load suggestions first</EmptyFilterNote>
              ) : (
                <CheckList>
                  {availableGenres.map(g => (
                    <CheckRow key={g.id}>
                      <input
                        type="checkbox"
                        checked={filterGenres.has(g.id)}
                        onChange={() => toggleFilterGenre(g.id)}
                      />
                      {g.name}
                    </CheckRow>
                  ))}
                </CheckList>
              )}
            </FilterGroupBody>
          </FilterGroup>

          {/* MPAA rating filter */}
          <FilterGroup>
            <FilterGroupTitle onClick={() => toggleGroup('mpaa')}>
              MPAA Rating
              <CollapseArrow $open={!collapsedGroups.has('mpaa')}>▼</CollapseArrow>
            </FilterGroupTitle>
            <FilterGroupBody $open={!collapsedGroups.has('mpaa')}>
              <CheckList style={{ maxHeight: 'none' }}>
                {['G', 'PG', 'PG-13', 'R', 'NC-17'].map(r => (
                  <CheckRow key={r}>
                    <input
                      type="checkbox"
                      checked={filterMpaaRatings.has(r)}
                      onChange={() => toggleFilterMpaa(r)}
                    />
                    {r}
                  </CheckRow>
                ))}
              </CheckList>
              {filterMpaaRatings.size === 0 && (
                <EmptyFilterNote style={{ marginTop: '0.35rem' }}>All ratings shown</EmptyFilterNote>
              )}
            </FilterGroupBody>
          </FilterGroup>

          {visibleSuggestions.length > 0 && hasActiveFilters && (
            <FilterResultCount>
              {filteredSuggestions.length} of {visibleSuggestions.length} shown
            </FilterResultCount>
          )}
        </FilterPane>

        {/* Main content */}
        <MainContent>
          <StatsCard>
            <StatsGrid>
              <StatItem>
                <StatNumber>{activeSuggestions.length}</StatNumber>
                <StatLabel>Active</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{watchedCount}</StatNumber>
                <StatLabel>Watched</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{rejectedCount}</StatNumber>
                <StatLabel>Rejected</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{suggestions.length}</StatNumber>
                <StatLabel>Total</StatLabel>
              </StatItem>
            </StatsGrid>
          </StatsCard>

          {refreshProgress && (
            <div style={{ margin: '0.5rem 0' }}>
              <ProgressBar>
                <ProgressFill $pct={Math.round((refreshProgress.done / refreshProgress.total) * 100)} />
              </ProgressBar>
              <ProgressLabel>
                Refreshing providers… {refreshProgress.done} / {refreshProgress.total}
              </ProgressLabel>
            </div>
          )}
          {actionMessage && (
            <ErrorMessage style={{ background: '#c6f6d5', color: '#276749' }}>
              {actionMessage}
            </ErrorMessage>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {loading && <LoadingSpinner>Loading recommendations...</LoadingSpinner>}

          {!loading && suggestions.length === 0 && (
            <NoSuggestions>
              <h3>No recommendations yet!</h3>
              <p>Rate some movies to get personalized recommendations.</p>
            </NoSuggestions>
          )}

          {!loading && suggestions.length > 0 && visibleSuggestions.length === 0 && (
            <NoSuggestions>
              <h3>No recommendations on your streaming services</h3>
              <p>Uncheck "My providers only" to see all recommendations, or update your providers in Settings.</p>
            </NoSuggestions>
          )}

          {!loading && visibleSuggestions.length > 0 && filteredSuggestions.length === 0 && (
            <NoSuggestions>
              <h3>No results match your filters</h3>
              <p>Try adjusting or clearing the filters on the left.</p>
            </NoSuggestions>
          )}

          {!loading && (
            <>
              <GroupByBar>
                <label htmlFor="order-by-select">Order by:</label>
                <GroupBySelect
                  id="order-by-select"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortBy)}
                >
                  <option value="points">Best match</option>
                  <option value="title">Title</option>
                  <option value="genre">Genre</option>
                  <option value="score">TMDb rating</option>
                </GroupBySelect>
                <label htmlFor="group-by-select" style={{ marginLeft: '0.75rem' }}>Group by:</label>
                <GroupBySelect
                  id="group-by-select"
                  value={groupBy}
                  onChange={e => { setGroupBy(e.target.value as GroupBy); setCollapsedResultGroups(new Set()); }}
                >
                  <option value="none">None</option>
                  <option value="movie">Movie</option>
                  <option value="director">Director</option>
                  <option value="genre">Genre</option>
                </GroupBySelect>
                <label htmlFor="method-select" style={{ marginLeft: '0.75rem' }}>Method:</label>
                <GroupBySelect
                  id="method-select"
                  value={method}
                  onChange={e => setMethod(e.target.value as Method)}
                  title="Filters the list to that method's suggestions, and is what the next Generate click will use"
                >
                  <option value="all">All Sources</option>
                  <option value="similar">Similar Movies</option>
                  <option value="director">Director</option>
                  <option value="actor">Actor/Actress</option>
                  <option value="studio">Studio</option>
                </GroupBySelect>
                {groupBy !== 'none' && filteredSuggestions.length > 0 && (
                  <>
                    <ExpandCollapseButton onClick={expandAll}>Expand All</ExpandCollapseButton>
                    <ExpandCollapseButton onClick={collapseAll}>Collapse All</ExpandCollapseButton>
                  </>
                )}
                <Button
                  $variant="secondary"
                  style={{ marginLeft: 'auto' }}
                  onClick={handleRefreshProviders}
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh Providers'}
                </Button>
                {refreshing && (
                  <Button $variant="danger" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </GroupByBar>

              {filteredSuggestions.length > 0 && (
                groupBy === 'none' ? (
                  <SuggestionsGrid>
                    {sortedSuggestions.map(s => renderCard(s))}
                  </SuggestionsGrid>
                ) : (
                  groupedSuggestions.map(([groupKey, items]) => {
                    const isOpen = !collapsedResultGroups.has(groupKey);
                    return (
                      <GroupSection key={groupKey}>
                        <GroupHeader onClick={() => toggleResultGroup(groupKey)}>
                          <CollapseArrow $open={isOpen}>▼</CollapseArrow>
                          {groupBy === 'movie'    ? `Because you liked: ${groupKey}` :
                           groupBy === 'director' ? `Films by ${groupKey}` :
                           groupKey}
                          <GroupCount>({items.length})</GroupCount>
                        </GroupHeader>
                        {isOpen && (
                          <SuggestionsGrid>
                            {items.map(s => renderCard(s))}
                          </SuggestionsGrid>
                        )}
                      </GroupSection>
                    );
                  })
                )
              )}
            </>
          )}
        </MainContent>
      </PageLayout>

      {selectedMovie && (
        <MovieRatingDialog
          movie={selectedMovie}
          isOpen={isRatingDialogOpen}
          onClose={() => {
            pendingRateSuggestionId.current = null;
            setIsRatingDialogOpen(false);
            setSelectedMovie(null);
          }}
          onRatingSubmitted={() => {
            const sid = pendingRateSuggestionId.current;
            pendingRateSuggestionId.current = null;
            if (sid !== null) handleMarkAsWatched(sid);
          }}
        />
      )}

      {watchlistMovie && (
        <AddToWatchlistModal
          movie={{
            tmdb_id: watchlistMovie.tmdb_id,
            title: watchlistMovie.title,
            release_date: watchlistMovie.release_date,
            imdb_score: watchlistMovie.imdb_score,
            director_name: watchlistMovie.director_name,
            mpaa_rating: watchlistMovie.mpaa_rating,
            watch_providers: watchlistMovie.watch_providers,
          }}
          onClose={() => setWatchlistMovie(null)}
          onAdded={(watchlistId, watchlistName) => {
            setWatchlistMembership(prev => ({
              ...prev,
              [watchlistMovie.tmdb_id]: [
                ...(prev[watchlistMovie.tmdb_id] ?? []),
                watchlistName,
              ],
            }));
            setWatchlistMovie(null);
          }}
        />
      )}

      {basedOnPopup && (
        <PopupOverlay onClick={() => setBasedOnPopup(null)}>
          <PopupBox onClick={e => e.stopPropagation()}>
            <PopupTitle>{basedOnPopup.label}:</PopupTitle>
            <PopupList>
              {basedOnPopup.movies.map(title => <li key={title}>{title}</li>)}
            </PopupList>
            <PopupCloseButton onClick={() => setBasedOnPopup(null)}>Close</PopupCloseButton>
          </PopupBox>
        </PopupOverlay>
      )}
    </SuggestionsContainer>
  );
};

export default SuggestionsPage;
